import { TextField } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useScrollToQuestion } from '@/hooks/useScrollToQuestion.js';

import { useStyles as useAnamnesisStyles } from '@/pages/HealthProfileAnamnesis/YourBodyComponents/YourBodyStyles.js';

import { TextValidation } from '@/utils/constants.js';
import { isEmail } from '@/utils/formUtils.js';

import { useCustomAnamnesisStore } from '@/store.js';

import { useCustomAnamnesisStyles } from '../customAnamnesisStyles.js';
import { useTextFieldQuestionStyles } from './QuestionTypesStyles.js';

const TextfieldQuestion = ({
    question,
    answer,
    setAnswer,
    isSubQuestion = false,
    hideTitle = false,
    disabled = false,
    type = 'text',
    autoComplete = 'off',
    customValidation = () => true,
    customValidationErrorMessage = undefined,
    multiline = false,
    maxRows = 1,
    height = '3.75rem',
    required = false,
    showRequiredIndicator = true,
}) => {
    const { t } = useTranslation();
    const anamnesisClasses = useAnamnesisStyles();
    const classes = useTextFieldQuestionStyles();
    const [isValidAnswer, setIsValidAnswer] = useState(false);
    const [showCustomValidationError, setShowCustomValidationError] = useState(false);
    const [showRequiredError, setShowRequiredError] = useState(false);
    const customAnamnesisStyles = useCustomAnamnesisStyles();
    const highlightedQuestions = useCustomAnamnesisStore((state) => state.highlightedQuestions);
    const questionRef = useScrollToQuestion(question);

    useEffect(() => {
        const currentAnswer = !isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text];

        if (!currentAnswer) {
            setShowCustomValidationError(false);
            setIsValidAnswer(true);
            return;
        }

        switch (question.textfield_validation) {
            case TextValidation.EMAIL:
                setIsValidAnswer(isEmail(currentAnswer));
                break;
            case TextValidation.NONE:
            default:
                setIsValidAnswer(true);
                break;
        }

        if (!customValidation(currentAnswer)) {
            setShowCustomValidationError(true);
        } else {
            setShowCustomValidationError(false);
        }
    }, [answer, isSubQuestion, customValidation, question]);

    const onChange = (e) => {
        if (!isSubQuestion) {
            setAnswer(e.target.value);
        } else {
            setAnswer({
                ...answer,
                subQuestions: {
                    ...answer.subQuestions,
                    [question.question_key[0].text]: e.target.value,
                },
            });
        }

        if (e.target.value && highlightedQuestions[question.question_key[0].text]) {
            useCustomAnamnesisStore.setState({
                highlightedQuestions: {
                    ...highlightedQuestions,
                    [question.question_key[0].text]: false,
                },
            });
        }

        // Clear required error when user starts typing
        if (e.target.value && showRequiredError) {
            setShowRequiredError(false);
        }
    };

    const onBlur = () => {
        const currentAnswer = !isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text];
        if (required && !currentAnswer) {
            setShowRequiredError(true);
        }
    };

    const hasValidationError = () => {
        const currentAnswer = !isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text];
        return currentAnswer && (!isValidAnswer || !customValidation(currentAnswer));
    };

    return (
        <div
            ref={questionRef}
            data-testid={`question-${question.question_key[0].text}`}
            className={clsx(
                customAnamnesisStyles.defaultQuestion,
                required &&
                    showRequiredIndicator &&
                    !highlightedQuestions[question.question_key[0].text] &&
                    customAnamnesisStyles.requiredIndicator,
                highlightedQuestions[question.question_key[0].text] && customAnamnesisStyles.highlight
            )}
        >
            {!isSubQuestion && !hideTitle && (
                <Typography style={{ marginBottom: '1rem' }} className={classes.title}>
                    {question.title[0].text}
                </Typography>
            )}
            <TextField
                label={!isSubQuestion ? question.label[0]?.text : question.title[0].text}
                disabled={disabled}
                variant="filled"
                autoComplete={autoComplete}
                type={type}
                required={required}
                value={(!isSubQuestion ? answer : answer?.subQuestions[question.question_key[0].text]) ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                classes={{
                    root: clsx(
                        hasValidationError() && classes.error,
                        showRequiredError && classes.error,
                        required && classes.requiredLabel
                    ),
                }}
                style={{ width: '100%', height: height }}
                InputLabelProps={{
                    classes: {
                        root: anamnesisClasses.inputLabel,
                        focused: 'focused',
                    },
                }}
                InputProps={{
                    classes: {
                        root: anamnesisClasses.filledInputRoot,
                        focused: anamnesisClasses.fieldFocused,
                    },
                }}
                multiline={multiline}
                maxRows={maxRows}
            />
            {showCustomValidationError && customValidationErrorMessage && (
                <Typography className={classes.errorText}>{customValidationErrorMessage}</Typography>
            )}
            {showRequiredError && (
                <Typography className={classes.errorText}>
                    {customValidationErrorMessage || t('RequiredField')}
                </Typography>
            )}
        </div>
    );
};

export default TextfieldQuestion;
